import { create } from "zustand";
import { persist } from "zustand/middleware";

/** A theme that resolves to real tokens — everything except the `system` alias. */
export type ResolvedTheme =
  | "light"
  | "light-glass"
  | "dark"
  | "dark-black"
  | "dark-glass"
  | "dark-teal";

export type Theme = ResolvedTheme | "system";

/** Every theme class we ever put on `<html>`. */
export const RESOLVED_THEMES: readonly ResolvedTheme[] = [
  "light",
  "light-glass",
  "dark",
  "dark-black",
  "dark-glass",
  "dark-teal",
] as const;

const LIGHT_THEMES = new Set<ResolvedTheme>(["light", "light-glass"]);

/**
 * Whether a resolved theme paints a dark canvas.
 *
 * Call sites used to test `getEffectiveTheme() !== "light"`, which silently
 * classified every new theme as dark. That was correct only while `light` was
 * the sole light theme; `light-glass` broke it, and picking a dark logo for a
 * light canvas is the visible symptom. Add light themes to `LIGHT_THEMES`, not
 * to a comparison at the call site.
 */
export function isDarkTheme(theme: ResolvedTheme): boolean {
  return !LIGHT_THEMES.has(theme);
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  getEffectiveTheme: () => ResolvedTheme;
  /** Coarse light/dark mode of the active theme — for asset and chart choices. */
  getEffectiveMode: () => "light" | "dark";
}

/**
 * The OS preference maps to the glass pair, which are the product defaults —
 * not to the plain `light`/`dark` themes, which remain explicit opt-ins.
 */
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined") return "light-glass";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark-glass"
    : "light-glass";
};

/** Apply theme classes to `<html>` — shared with `main.tsx` pre-hydration bootstrap. */
export function applyThemeToDocument(theme: Theme) {
  const root = document.documentElement;
  // Derived from RESOLVED_THEMES so a new theme cannot be added without also
  // being removed here — omitting it silently stacks theme classes.
  root.classList.remove(...RESOLVED_THEMES);

  if (theme === "system") {
    const systemTheme = getSystemTheme();
    root.classList.add(systemTheme);
  } else {
    root.classList.add(theme);
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      // Must stay in step with the pre-hydration bootstrap in `main.tsx`, or
      // every load flashes the old theme before rehydrate repaints.
      theme: "dark-glass",
      setTheme: (theme: Theme) => {
        set({ theme });
        applyThemeToDocument(theme);
      },
      getEffectiveTheme: () => {
        const { theme } = get();
        if (theme === "system") {
          return getSystemTheme();
        }
        return theme;
      },
      getEffectiveMode: () =>
        isDarkTheme(get().getEffectiveTheme()) ? "dark" : "light",
    }),
    {
      name: "everythingcloud-theme",
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state, error) => {
        if (error || !state?.theme) return;
        applyThemeToDocument(state.theme);
      },
    },
  ),
);

if (typeof window !== "undefined") {
  useThemeStore.persist.onFinishHydration(() => {
    applyThemeToDocument(useThemeStore.getState().theme);
  });
}

// Listen for system theme changes
if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      const { theme } = useThemeStore.getState();
      if (theme === "system") {
        applyThemeToDocument("system");
      }
    });
}
